<script setup lang="ts">
import { useCaseQuery } from '@/data/Cases/useCaseQuery'
import { useUpdateCase } from '@/data/Cases/useUpdateCase'
import DeleteCaseDialog from '@/modules/Cases/DeleteCaseDialog.vue'
import { copyToClipboard } from '@/shared/clipboard'
import { toast } from '@/shared/toast'
import { ApiError } from '@/shared/utils/error'
import { invariant, nonNullable } from '@/shared/utils/typeAssertions'
import AppHeader from '@/sharedComponents/AppHeader.vue'
import CrumbChevron from '@/sharedComponents/CrumbChevron.vue'
import CrumbItem from '@/sharedComponents/CrumbItem.vue'
import SidebarToggle from '@/sharedComponents/SidebarToggle.vue'
import IconButton from '@/uiKit/IconButton.vue'
import InlineTextField from '@/uiKit/InlineTextField.vue'
import Menu from '@/uiKit/Menu'
import { computed, ref, toRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { FALLBACK_CASE_NAME } from './serializers'
import { useCaseSidebarRoutingToggle } from './Sidebar/useCaseSidebarRoutingToggle'
const props = defineProps<{ caseId?: string; workspaceId: string }>()

const localCaseName = ref('')

const { data: caseData, error } = useCaseQuery(toRef(props, 'caseId'), toRef(props, 'workspaceId'))
const isNotFound = computed(
  () => error instanceof ApiError && (error.status === 404 || error.status === 400),
)

watch(
  () => caseData.value?.name,
  (newName) => {
    if (newName === undefined) {
      return
    }

    localCaseName.value = newName
  },
  { immediate: true },
)

const isRenaming = ref(false)

const { mutate: updateCaseMutation } = useUpdateCase({
  onError: () => {
    toast.error('Failed to update case name. If this issue persists, please contact support.')
  },
  onSuccess: () => {
    isRenaming.value = false
  },
})

const saveName = (newName: string) => {
  updateCaseMutation({
    caseId: nonNullable(props.caseId),
    name: newName,
    workspaceId: props.workspaceId,
  })
}

const undoRename = () => {
  invariant(caseData.value, 'No active case when undoing rename')
  isRenaming.value = false
  localCaseName.value = caseData.value.name || 'Untitled case'
}

const route = useRoute()
const isNewCaseRoute = computed(() => route.name === 'NewCase')

const isDeleting = ref(false)

const { isSidebarOpen, closeSidebar, openSidebar } = useCaseSidebarRoutingToggle()
</script>

<template>
  <AppHeader>
    <template #crumbs>
      <CrumbItem
        rounded
        :to="{ name: 'WorkspaceHomepage' }"
      >
        Home
      </CrumbItem>
      <CrumbChevron />

      <InlineTextField
        v-if="isRenaming"
        class="min-w-fit focus-within:[&>input]:placeholder:opacity-0"
        size="sm"
        aria-label="Case name"
        autofocus
        :value="localCaseName"
        :placeholder="FALLBACK_CASE_NAME"
        :input-attrs="{ size: Math.max(5, localCaseName.length).toString() }"
        @input="localCaseName = $event"
        @submit="saveName"
        @blur="saveName(localCaseName)"
        @esc="undoRename"
      />

      <template v-else-if="caseData">
        <CrumbItem active>
          {{ caseId ? localCaseName : 'New case' }}
        </CrumbItem>
        <Menu.Root v-slot="{ getTriggerProps }">
          <IconButton
            size="sm"
            aria-label="Open case menu"
            variant="transparent"
            v-bind="{ ...getTriggerProps(), ...$attrs }"
            class="text-text-subtle"
            icon="more-dots"
          />
          <Menu.Content class="min-w-56">
            <Menu.Item
              label="Copy case ID"
              icon="copy"
              @select="copyToClipboard('ID', caseData.id)"
            />
            <Menu.Divider />
            <Menu.Item
              label="Rename case"
              icon="edit"
              @select="isRenaming = true"
            />
            <Menu.Item
              critical
              label="Delete case"
              icon="trash"
              @select="isDeleting = true"
            />
          </Menu.Content>
        </Menu.Root>
      </template>

      <CrumbItem
        v-else-if="isNewCaseRoute"
        active
      >
        {{ caseId ? localCaseName : 'New case' }}
      </CrumbItem>

      <CrumbItem
        v-else-if="isNotFound"
        active
      >
        Case not found
      </CrumbItem>

      <DeleteCaseDialog
        v-if="caseId"
        v-model:open="isDeleting"
        :case-id="caseId"
        :workspace-id="workspaceId"
      />
    </template>

    <template
      v-if="caseId"
      #actions
    >
      <div class="flex flex-row gap-2">
        <SidebarToggle
          open-tooltip="Open case sidebar"
          close-tooltip="Close case sidebar"
          side="right"
          label="Toggle case sidebar"
          :is-open="isSidebarOpen"
          @toggle="isSidebarOpen ? closeSidebar() : openSidebar()"
        />
      </div>
    </template>
  </AppHeader>
</template>
